import React, { useCallback, useEffect, useState } from "react";
import { api, fmtNum, fmtMoney } from "@/lib/api";
export default function Signals() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("ALL");
  const [selected, setSelected] = useState(null);
  const [riskExpl, setRiskExpl] = useState(null);
  const [riskBusy, setRiskBusy] = useState(false);
  // Map: signal_id -> shadow P&L row (only populated for risk-limit rejects).
  const [shadowById, setShadowById] = useState({});

  const loadRiskExplanation = useCallback(async (sig) => {
    if (!sig?.id) {
      setRiskExpl(null);
      return;
    }
    setRiskBusy(true);
    setRiskExpl(null);
    try {
      const { data } = await api.get(`/signals/${sig.id}/risk-explanation`);
      setRiskExpl(data);
    } catch (e) {
      setRiskExpl({ error: e?.response?.data?.detail || "Unavailable" });
    } finally {
      setRiskBusy(false);
    }
  }, []);

  useEffect(() => {
    if (selected) loadRiskExplanation(selected);
    else setRiskExpl(null);
  }, [selected, loadRiskExplanation]);

  const load = useCallback(async () => {
    const [s, c] = await Promise.all([
      api.get("/signals?limit=200"),
      api.get("/candidates?limit=300"),
    ]);
    // merge: signals are richer; add candidates that didn't get promoted
    const signals = s.data.map((x) => ({ ...x, kind: "SIGNAL" }));
    const sigKeys = new Set(signals.map((x) => `${x.underlying}|${x.candle_timestamp}`));
    const extras = c.data
      .filter((x) => !sigKeys.has(`${x.underlying}|${x.candle_timestamp}`))
      .map((x) => ({ ...x, kind: "CANDIDATE" }));
    setRows([...signals, ...extras].sort((a, b) => (a.candle_timestamp < b.candle_timestamp ? 1 : -1)));
    // Refresh shadow P&L map (only risk-limit rejects populate here).
    try {
      const { data: shadow } = await api.get("/shadow-rejected", { params: { days: 90 } });
      const m = {};
      (shadow.rows || []).forEach((r) => { if (r.signal_id) m[r.signal_id] = r; });
      setShadowById(m);
    } catch (e) {
      // Endpoint may not be deployed yet on older backends — dev-only log.
      if (process.env.NODE_ENV === "development") {
        // eslint-disable-next-line no-console
        console.warn("shadow load skipped:", e?.response?.status || e?.message);
      }
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [load]);

  const filtered = rows.filter((r) => {
    if (filter === "ACCEPTED") return r.accepted;
    if (filter === "REJECTED") return !r.accepted;
    return true;
  });

  return (
    <div className="space-y-4" data-testid="signals-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-medium tracking-tight">Signals</h1>
          <p className="text-xs text-slate-500 uppercase tracking-[0.15em] mt-1">
            Accepted &amp; rejected candidates with full indicator snapshot
          </p>
        </div>
        <div className="flex gap-1 text-xs items-center">
          {["ALL", "ACCEPTED", "REJECTED"].map((f) => (
            <button
              key={f}
              data-testid={`filter-${f.toLowerCase()}`}
              onClick={() => setFilter(f)}
              className={`px-3 py-1.5 rounded-sm uppercase tracking-[0.15em] ${
                filter === f
                  ? "bg-blue-600 text-white"
                  : "border border-slate-700 text-slate-400 hover:bg-slate-800/50"
              }`}
            >
              {f}
            </button>
          ))}
          <span className="mx-2 w-px h-6 bg-slate-800" />
          <a
            data-testid="download-signals-csv"
            href={`${process.env.REACT_APP_BACKEND_URL || ""}/api/signals/export?format=csv&days=30`}
            className="px-3 py-1.5 rounded-sm uppercase tracking-[0.15em] border border-emerald-700 bg-emerald-900/20 text-emerald-300 hover:bg-emerald-800/30"
            title="Download last 30 days of signals as CSV for offline / ChatGPT analysis"
          >
            Download CSV
          </a>
          <a
            data-testid="download-signals-json"
            href={`${process.env.REACT_APP_BACKEND_URL || ""}/api/signals/export?format=json&days=30`}
            target="_blank"
            rel="noreferrer"
            className="px-3 py-1.5 rounded-sm uppercase tracking-[0.15em] border border-slate-700 text-slate-400 hover:bg-slate-800/50"
            title="View last 30 days of signals as JSON"
          >
            View JSON
          </a>
        </div>
      </div>

      <div className="surface rounded-sm overflow-auto max-h-[65vh]">
        <table className="dt">
          <thead>
            <tr>
              <th>Time (IST)</th>
              <th>Underlying</th>
              <th>Dir</th>
              <th>Option</th>
              <th className="num">Score</th>
              <th className="num">ADX</th>
              <th className="num">Vol×</th>
              <th className="num">ATR</th>
              <th>Status</th>
              <th>Rejection</th>
              <th className="num" title="Counterfactual: what this trade would have made if the risk gate hadn't blocked it. Only populated for risk-limit rejections.">Shadow P&amp;L</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <tr
                key={r.id || `${r.underlying}-${r.candle_timestamp}-${r.kind}`}
                onClick={() => setSelected(r)}
                className="cursor-pointer"
                data-testid="signal-row"
              >
                <td className="font-mono text-slate-300">{r.candle_timestamp}</td>
                <td>{r.underlying}</td>
                <td className={r.direction === "LONG" ? "text-pos" : r.direction === "SHORT" ? "text-neg" : ""}>{r.direction || "—"}</td>
                <td className="font-mono text-slate-300">{r.option?.tradingsymbol || r.option_type || "—"}</td>
                <td className="num font-mono">{fmtNum(r.score, 3)}</td>
                <td className="num font-mono">{fmtNum(r.indicators?.adx14, 1)}</td>
                <td className="num font-mono">{fmtNum(r.indicators?.volume_ratio, 2)}</td>
                <td className="num font-mono">{fmtNum(r.indicators?.atr14, 1)}</td>
                <td className={`text-[10px] uppercase tracking-[0.15em] ${r.accepted ? "text-pos" : "text-slate-500"}`}>
                  {r.accepted ? "ACCEPTED" : "REJECTED"}
                </td>
                <td className="text-slate-400 text-[11px]">{r.rejection_code || "—"}</td>
                <td className="num font-mono text-[11px]" data-testid="signal-shadow-pnl">
                  {(() => {
                    const sh = shadowById[r.id];
                    if (!sh) return <span className="text-slate-600">—</span>;
                    const pos = (sh.net_pnl || 0) > 0;
                    return (
                      <span className={pos ? "text-pos" : "text-neg"} title={`${sh.exit_reason} · exit ${sh.exit_price?.toFixed(2)}`}>
                        {fmtMoney(sh.net_pnl)}
                      </span>
                    );
                  })()}
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan="11" className="text-center text-slate-500 py-8">No signals to display.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {selected && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-md z-40 flex items-center justify-center p-4" data-testid="signal-detail-modal">
          <div className="surface rounded-sm max-w-3xl w-full max-h-[80vh] overflow-auto p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Signal Detail</div>
                <div className="text-lg font-mono">{selected.underlying} · {selected.candle_timestamp}</div>
              </div>
              <button
                onClick={() => setSelected(null)}
                className="text-slate-400 hover:text-white text-2xl leading-none"
              >×</button>
            </div>
            <div className="grid grid-cols-2 gap-4 text-xs">
              <div>
                <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500 mb-2">Decision</div>
                <div className="space-y-1 font-mono">
                  <div>Accepted: <span className={selected.accepted ? "text-pos" : "text-neg"}>{String(selected.accepted)}</span></div>
                  <div>Direction: {selected.direction || "—"}</div>
                  <div>Option: {selected.option_type || "—"}</div>
                  <div>Score: {fmtNum(selected.score, 4)}</div>
                  <div className="pt-2 text-slate-400">Rejection code: {selected.rejection_code || "—"}</div>
                  <div className="text-slate-400">Reason: {selected.rejection_reason || "—"}</div>
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500 mb-2">Indicators</div>
                <div className="space-y-0.5 font-mono">
                  {Object.entries(selected.indicators || {}).map(([k, v]) => (
                    <div key={k} className="flex justify-between">
                      <span className="text-slate-500">{k}</span>
                      <span>{fmtNum(v)}</span>
                    </div>
                  ))}
                </div>
              </div>
              {selected.ml_probability != null && (
                <div className="col-span-2">
                  <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500 mb-2">ML Prediction</div>
                  <div className="grid grid-cols-3 gap-x-4 gap-y-1 font-mono mb-3">
                    <div className="flex justify-between border-b border-slate-800 py-1"><span className="text-slate-500">Probability</span><span className={selected.ml_probability >= 0.55 ? "text-pos" : "text-slate-300"}>{(selected.ml_probability * 100).toFixed(1)}%</span></div>
                    <div className="flex justify-between border-b border-slate-800 py-1"><span className="text-slate-500">Expected P&amp;L</span><span>{fmtMoney(selected.ml_expected_pnl)}</span></div>
                    <div className="flex justify-between border-b border-slate-800 py-1"><span className="text-slate-500">Recommend</span><span className={selected.ml_recommend ? "text-pos" : "text-neg"}>{String(selected.ml_recommend)}</span></div>
                  </div>
                  {selected.ml_top_contributions?.length > 0 && (
                    <>
                      <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500 mb-2">Top Triggers (SHAP)</div>
                      <div className="space-y-1">
                        {selected.ml_top_contributions.slice(0, 5).map((c) => (
                          <div key={c.feature} className="flex items-center gap-3 text-xs">
                            <span className="font-mono text-slate-300 min-w-[180px]">{c.feature}</span>
                            <div className="flex-1 h-2 bg-slate-800 rounded-sm overflow-hidden relative">
                              <div
                                className={c.contribution >= 0 ? "bg-emerald-500" : "bg-red-500"}
                                style={{
                                  height: "100%",
                                  width: `${Math.min(100, Math.abs(c.contribution) * 100)}%`,
                                  marginLeft: c.contribution < 0 ? "auto" : 0,
                                }}
                              />
                            </div>
                            <span className={`font-mono text-[11px] ${c.contribution >= 0 ? "text-pos" : "text-neg"}`}>
                              {c.contribution >= 0 ? "+" : ""}{c.contribution.toFixed(3)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}
              {selected.option && Object.keys(selected.option).length > 0 && (
                <div className="col-span-2">
                  <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500 mb-2">Selected Option</div>
                  <div className="grid grid-cols-3 gap-x-4 gap-y-1 font-mono">
                    {Object.entries(selected.option).map(([k, v]) => (
                      <div key={k} className="flex justify-between border-b border-slate-800 py-1">
                        <span className="text-slate-500">{k}</span>
                        <span>{typeof v === "number" ? fmtNum(v, 4) : String(v)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {selected.sizing && Object.keys(selected.sizing).length > 0 && (
                <div className="col-span-2">
                  <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500 mb-2">Sizing</div>
                  <div className="grid grid-cols-3 gap-x-4 gap-y-1 font-mono">
                    {Object.entries(selected.sizing).map(([k, v]) => (
                      <div key={k} className="flex justify-between border-b border-slate-800 py-1">
                        <span className="text-slate-500">{k}</span>
                        <span>{fmtNum(v)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {/* V2 Phase 4: Risk Explanation drawer */}
              {selected.id && (
                <div className="col-span-2" data-testid="risk-explanation-drawer">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-[10px] uppercase tracking-[0.15em] text-blue-300">
                      V2 Risk Explanation
                    </div>
                    {riskBusy && (
                      <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500">
                        Computing…
                      </div>
                    )}
                  </div>
                  {riskExpl?.error && (
                    <div className="text-xs text-amber-400 font-mono">{riskExpl.error}</div>
                  )}
                  {riskExpl && !riskExpl.error && (
                    <>
                      <div className="grid grid-cols-3 gap-x-4 gap-y-1 font-mono text-xs mb-3">
                        <div className="flex justify-between border-b border-slate-800 py-1"><span className="text-slate-500">Underlying stop</span><span data-testid="risk-underlying-stop">{fmtNum(riskExpl.underlying_stop, 2)}</span></div>
                        <div className="flex justify-between border-b border-slate-800 py-1"><span className="text-slate-500">Modeled option stop</span><span data-testid="risk-option-stop">₹{fmtNum(riskExpl.option?.modeled_stop_price, 2)}</span></div>
                        <div className="flex justify-between border-b border-slate-800 py-1"><span className="text-slate-500">Δ / Γ / Θ</span><span>{fmtNum(riskExpl.greeks?.delta, 3)} / {fmtNum(riskExpl.greeks?.gamma, 4)} / {fmtNum(riskExpl.greeks?.theta_per_day, 2)}</span></div>
                        <div className="flex justify-between border-b border-slate-800 py-1"><span className="text-slate-500">Gross loss</span><span>{fmtMoney(riskExpl.risk?.gross_option_loss)}</span></div>
                        <div className="flex justify-between border-b border-slate-800 py-1"><span className="text-slate-500">Charges (round trip)</span><span>{fmtMoney((riskExpl.risk?.entry_charges || 0) + (riskExpl.risk?.exit_charges || 0))}</span></div>
                        <div className="flex justify-between border-b border-slate-800 py-1"><span className="text-slate-500">Slippage</span><span>{fmtMoney((riskExpl.risk?.entry_slippage || 0) + (riskExpl.risk?.exit_slippage || 0))}</span></div>
                        <div className="flex justify-between border-b border-slate-800 py-1"><span className="text-slate-500">Net trade risk</span><span data-testid="risk-net-trade-risk">{fmtMoney(riskExpl.risk?.net_trade_risk)}</span></div>
                        <div className="flex justify-between border-b border-slate-800 py-1"><span className="text-slate-500">Net one-lot risk</span><span>{fmtMoney(riskExpl.risk?.net_one_lot_risk)}</span></div>
                        <div className="flex justify-between border-b border-slate-800 py-1"><span className="text-slate-500">Risk budget</span><span className={riskExpl.risk?.one_lot_fits_budget ? "text-pos" : "text-neg"} data-testid="risk-budget-fit">{fmtMoney(riskExpl.risk?.risk_budget)}</span></div>
                        <div className="flex justify-between border-b border-slate-800 py-1"><span className="text-slate-500">Min capital</span><span>{fmtMoney(riskExpl.risk?.min_capital_required)}</span></div>
                        <div className="flex justify-between border-b border-slate-800 py-1"><span className="text-slate-500">Expected reward</span><span>{fmtMoney(riskExpl.risk?.expected_net_reward)}</span></div>
                        <div className="flex justify-between border-b border-slate-800 py-1"><span className="text-slate-500">R:R ratio</span><span>{fmtNum(riskExpl.risk?.net_reward_risk_ratio, 2)}</span></div>
                      </div>
                      <div className="border-l-2 border-l-blue-500 pl-3 text-[11px] text-slate-300 leading-relaxed space-y-1" data-testid="risk-explanation-lines">
                        {(riskExpl.explanation || []).map((line, i) => (
                          // Explanation lines are static text emitted in
                          // order; index is a stable identity since the
                          // list is fully replaced on each fetch.
                          // eslint-disable-next-line react/no-array-index-key
                          <div key={`expl-${i}`}>{line}</div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
